// ============================================
// SRS State Badge (shared)
// ============================================
//
// The color-coded circular badge (N / L / R / M) for a word's SRS state.
// Extracted from Flashcard's SRSIndicator so the bucket word-list rows can
// reuse the exact same visual without duplicating the stateConfig lookup.
//
// `animated` opts into the framer-motion `srsIndicator` variant used on the
// flashcard corner; list rows render the static (cheaper) variant.

import { m } from 'framer-motion';
import { srsIndicator } from '@/animations';
import { SRS_CONFIG } from '@/constants';
import { categoryFor } from '@/state/signals';
import type { SRSState } from '@/types';

interface SRSBadgeProps {
  state: SRSState;
  /** Size/position classes (default sizing left to the caller). */
  className?: string;
  /** Animate via the srsIndicator variant (flashcard corner). Default static. */
  animated?: boolean;
}

export function SRSBadge({ state, className = '', animated = false }: SRSBadgeProps) {
  const config = SRS_CONFIG.stateConfig[state];
  const base = 'rounded-full flex items-center justify-center text-xs font-bold';
  const style = {
    background: `linear-gradient(135deg, ${config.color} 0%, ${config.color}dd 100%)`,
    color: config.onColor
  };
  const title = `${config.name}: ${config.description}`;

  if (animated) {
    return (
      <m.div
        className={`${base} ${className}`}
        variants={srsIndicator}
        animate={categoryFor(state)}
        title={title}
        style={style}
      >
        {config.indicator}
      </m.div>
    );
  }

  return (
    <div className={`${base} ${className}`} title={title} style={style}>
      {config.indicator}
    </div>
  );
}
